import React, { useEffect, useRef, useState } from "react";
import {
  FALLBACK_URL,
  sanearUrlImagen,
  refrescarUrlImagenEvento,
} from "@/utils/imagenes";

// Tiempo máximo (ms) que esperamos a que una imagen cargue antes de asumir
// que falló silenciosamente (p.ej. bloqueo ORB/CORS de red donde el evento
// `onError` NUNCA se dispara). Las imágenes locales cargan en <500ms.
const WATCHDOG_MS = 2500;

/**
 * Imagen de evento centralizada · Sprint 54 · Fase B (+ ajuste UX).
 *
 * Punto único de resolución/presentación de la imagen principal de un evento
 * para TODAS las superficies (Inicio, detalle, Mis Boletos, etc.).
 *
 * Reglas UX:
 *  - Mientras se resuelve la URL firmada o la imagen aún carga, se muestra un
 *    SKELETON neutro (no la imagen genérica).
 *  - La imagen real sólo aparece cuando termina de cargar (fade-in).
 *  - La imagen genérica (fallback institucional) SÓLO se usa cuando se
 *    confirma que el evento no tiene imagen o cuando el fallo es definitivo.
 *  - Ante un fallo con `eventoId`, se intenta UNA sola renovación de URL
 *    firmada. Nunca hay bucles de reintento (máquina de estados por `ref`).
 *  - Detecta fallos por DOS vías (porque `onError` NO se dispara en bloqueos
 *    de red tipo ERR_BLOCKED_BY_ORB): (a) `onError`, y (b) watchdog por
 *    timeout que revisa `naturalWidth`.
 *
 * Props:
 *  - `src`      URL ya resuelta por el backend (puede venir vacía si aún no
 *               hidrató o si el evento no tiene imagen).
 *  - `eventoId` id del evento (para resolver/renovar la URL firmada).
 *  - `alt`      texto alternativo (accesibilidad).
 *  - resto de props (`className`, `loading`, ...) se pasan al <img>.
 */
export default function EventoImagen({
  src,
  eventoId,
  alt = "Evento",
  className = "",
  loading = "lazy",
  ...rest
}) {
  // phase: "loading" (skeleton) · "real" (imagen cargada) · "fallback" (genérica)
  const [phase, setPhase] = useState("loading");
  const [displaySrc, setDisplaySrc] = useState(null);
  // stage: 0 = url original · 1 = ya renovada una vez · 2 = terminal
  const stageRef = useRef(0);
  const phaseRef = useRef("loading");
  const imgRef = useRef(null);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // Resolución inicial cada vez que cambia la fuente.
  useEffect(() => {
    let cancelado = false;
    stageRef.current = 0;
    const saneada = sanearUrlImagen(src);
    const tieneSrc = !!(src && String(src).trim());
    const candidatoValido = tieneSrc && saneada !== FALLBACK_URL;

    if (candidatoValido) {
      // Tenemos URL: intentamos cargarla (skeleton hasta onLoad).
      setPhase("loading");
      setDisplaySrc(saneada);
      return () => {
        cancelado = true;
      };
    }

    if (!tieneSrc && eventoId) {
      // Desconocido: pedimos la URL firmada UNA vez antes de decidir.
      stageRef.current = 1;
      setPhase("loading");
      setDisplaySrc(null);
      refrescarUrlImagenEvento(eventoId)
        .then((fresca) => {
          if (cancelado) return;
          const s = sanearUrlImagen(fresca);
          if (fresca && s !== FALLBACK_URL) {
            const sep = s.includes("?") ? "&" : "?";
            setDisplaySrc(`${s}${sep}r=${Date.now()}`);
            setPhase("loading");
          } else {
            // Confirmado: el evento no tiene imagen.
            setPhase("fallback");
          }
        })
        .catch(() => {
          if (!cancelado) setPhase("fallback");
        });
      return () => {
        cancelado = true;
      };
    }

    // src vacío sin eventoId (no hay imagen) o URL insegura (fallo definitivo).
    setPhase("fallback");
    setDisplaySrc(null);
    return () => {
      cancelado = true;
    };
  }, [src, eventoId]);

  const recuperar = () => {
    if (phaseRef.current === "fallback") return;
    if (stageRef.current >= 2) {
      setPhase("fallback");
      return;
    }
    if (stageRef.current === 1) {
      // La URL renovada también falló → fallback definitivo.
      stageRef.current = 2;
      setPhase("fallback");
      return;
    }
    // Primer fallo (stage 0). Renovación controlada, una sola vez.
    if (eventoId) {
      stageRef.current = 1;
      setPhase("loading");
      refrescarUrlImagenEvento(eventoId)
        .then((fresca) => {
          const s = sanearUrlImagen(fresca);
          if (fresca && s !== FALLBACK_URL) {
            const sep = s.includes("?") ? "&" : "?";
            setDisplaySrc(`${s}${sep}r=${Date.now()}`);
            setPhase("loading");
          } else {
            stageRef.current = 2;
            setPhase("fallback");
          }
        })
        .catch(() => {
          stageRef.current = 2;
          setPhase("fallback");
        });
    } else {
      stageRef.current = 2;
      setPhase("fallback");
    }
  };

  // Watchdog: cubre fallos donde el navegador NO dispara `onError`
  // (bloqueos ORB/CORS). Sólo actúa cuando la carga TERMINÓ y falló
  // (`complete && naturalWidth === 0`). Para imágenes lazy aún fuera de
  // viewport (`complete === false`) re-sondea sin aplicar fallback.
  useEffect(() => {
    if (phase !== "loading" || !displaySrc) return;
    let cancelado = false;
    let timer = null;
    const revisar = () => {
      if (cancelado) return;
      const el = imgRef.current;
      if (!el) {
        timer = setTimeout(revisar, WATCHDOG_MS);
        return;
      }
      if (el.complete) {
        if (el.naturalWidth === 0) {
          recuperar();
        } else {
          setPhase("real");
        }
        return;
      }
      timer = setTimeout(revisar, WATCHDOG_MS);
    };
    timer = setTimeout(revisar, WATCHDOG_MS);
    return () => {
      cancelado = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, displaySrc]);

  const handleLoad = (e) => {
    if (e && e.currentTarget && e.currentTarget.naturalWidth > 0) {
      setPhase("real");
    }
  };

  const handleError = () => recuperar();

  const esFallback = phase === "fallback";
  const imgSrc = esFallback ? FALLBACK_URL : displaySrc;
  const mostrarImg = !!imgSrc;
  const visible = phase === "real" || esFallback;

  return (
    <>
      {phase === "loading" && (
        <div
          data-testid="evento-imagen-skeleton"
          aria-hidden="true"
          className="absolute inset-0 bg-muted animate-pulse"
        />
      )}
      {mostrarImg && (
        <img
          ref={imgRef}
          src={imgSrc}
          alt={alt}
          loading={loading}
          onLoad={handleLoad}
          onError={handleError}
          className={className}
          style={{
            opacity: visible ? 1 : 0,
            transition: "opacity 300ms ease",
          }}
          {...rest}
        />
      )}
    </>
  );
}
